import axios from 'axios';
import zigbeeConfig from '../config/zigbee';

export const API_BASE_URL = zigbeeConfig.apiBaseUrl;
export const API_ORIGIN = zigbeeConfig.apiOrigin;

/**
 * Plockar ut ett läsbart felmeddelande ur ett axios-fel eller ett vanligt Error.
 * Servern svarar med { message } (inloggade anrop) eller { error } (publika sidor).
 */
export const getErrorMessage = (err: any, fallback = 'Något gick fel'): string => {
    const data = err?.response?.data;
    return data?.message || data?.error || data?.details || err?.message || fallback;
};

// Restore saved token on page load
const savedToken = localStorage.getItem('iot_token');
if (savedToken) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Identify the current tenant on every request (derived from subdomain)
axios.defaults.headers.common['X-Tenant-Id'] = zigbeeConfig.tenantId;

const getLoginPath = (): string => '/login';

/** Senaste utloggning orsakad av 401. Visas på inloggningssidan för felsökning. */
export const LAST_LOGOUT_KEY = 'iot_last_logout';

// Global 401 interceptor – clears stale token and redirects to /login
axios.interceptors.response.use(
    r => r,
    err => {
        if (err?.response?.status === 401) {
            // Spara vilket anrop som loggade ut och varför, så att det syns på
            // inloggningssidan. Annars går det inte att se i PWA:n vad som hände.
            // Bara när man faktiskt var inloggad (fel kod vid inloggning är ingen
            // utloggning), och utan query-delen av adressen.
            try {
                if (localStorage.getItem('iot_token')) localStorage.setItem(LAST_LOGOUT_KEY, JSON.stringify({
                    at: new Date().toISOString(),
                    request: `${(err.config?.method ?? '').toUpperCase()} ${(err.config?.url ?? '').split('?')[0]}`,
                    message: err.response?.data?.message ?? '',
                }));
            } catch { /* ignorera */ }
            localStorage.removeItem('iot_token');
            localStorage.removeItem('iot_user');
            delete axios.defaults.headers.common['Authorization'];
            const loginPath = getLoginPath();
            if (window.location.pathname !== loginPath) {
                window.location.href = loginPath;
            }
        }
        return Promise.reject(err);
    }
);

/**
 * Helper function to handle API errors consistently
 */
export const handleApiError = (error: any, context: string): never => {
    console.error(`${context}:`, error);

    let errorMessage = `${context} — det gick inte`;

    if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        if (status === 400) {
            errorMessage = data.details || data.message || 'Uppgifterna gick inte att använda';
        } else if (status === 404) {
            errorMessage = data.message || 'Hittades inte';
        } else if (status === 500) {
            errorMessage = data.details || data.message || 'Något gick fel på servern';
        } else {
            errorMessage = data.message || `Serverfel (${status})`;
        }
    } else if (error.request) {
        // Request made but no response received — could be a timeout or connection failure
        if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
            errorMessage = `Det tog för lång tid — samordnaren eller enheten svarar kanske inte. Försök igen om några sekunder.`;
        } else {
            errorMessage = `Inget svar från servern på ${API_BASE_URL}. Kontrollera att tjänsten admin-api är igång och går att nå.`;
        }
    } else {
        // Error in request setup
        errorMessage = error.message || 'Okänt fel';
    }

    throw new Error(errorMessage);
};
